import React, { useMemo, useState } from 'react';
import IconLeftArrow from '../icons/IconLeftArrow';
import IconRightArrow from '../icons/IconRightArrow';
import { useTranslation } from 'react-i18next';

interface CalendarDay {
    date: Date;
    inCurrentMonth: boolean;
    key: string;
}

interface DatePickerTableProps {
    localeStr: string;
    dayRenderer: (day: CalendarDay) => React.ReactNode;
}

export const DatePickerTable: React.FC<DatePickerTableProps> = ({ localeStr, dayRenderer }) => {
    const { t } = useTranslation();

    const [currentDate, setCurrentDate] = useState(new Date());

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const { calendarDays, weekdayNames, monthName } = useMemo(() => {
        const locale = new Intl.Locale(localeStr);
        const firstDayOfWeek = locale.weekInfo.firstDay;

        const monthName = new Intl.DateTimeFormat(localeStr, {
            month: 'long',
            year: 'numeric',
        }).format(currentDate);

        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstOfMonth = new Date(year, month, 1);
        const firstDayOfMonthWeekday = firstOfMonth.getDay();

        // Adjust so the grid starts on the correct weekday
        const diff = (firstDayOfMonthWeekday - firstDayOfWeek + 7) % 7;
        const gridStart = new Date(firstOfMonth);
        gridStart.setDate(gridStart.getDate() - diff);

        // Get weekday headers, starting from locale-specific start day
        const weekdayNames = Array.from({ length: 7 }, (_, i) => {
            const weekday = (firstDayOfWeek + i) % 7;
            const tempDate = new Date(Date.UTC(2023, 0, 1 + weekday));
            return new Intl.DateTimeFormat(localeStr, {
                weekday: 'narrow',
                timeZone: 'UTC',
            }).format(tempDate);
        });

        const calendarDays: CalendarDay[][] = [];
        for (let week = 0; week < 6; week++) {
            const weekDays: CalendarDay[] = [];
            for (let day = 0; day < 7; day++) {
                const date = new Date(gridStart);
                date.setDate(gridStart.getDate() + week * 7 + day);
                weekDays.push({
                    date,
                    inCurrentMonth: date.getMonth() === month,
                    key: `${week}-${day}`,
                });
            }
            calendarDays.push(weekDays);
        }

        return { monthName, calendarDays, weekdayNames };
    }, [currentDate, localeStr]);

    return (
        <>
            <header className="fireknife-datepicker__header">
                <button
                    onClick={handlePrevMonth}
                    className="fireknife-datepicker__header-btn fireknife-btn"
                    aria-label={t('widgetDatePicker.prevMonth')}
                    title={t('widgetDatePicker.prevMonth')}
                >
                    <IconLeftArrow className="fireknife-datepicker__header-btn-icon" />
                </button>
                <div className="fireknife-datepicker__header-title">{monthName}</div>
                <button
                    onClick={handleNextMonth}
                    className="fireknife-datepicker__header-btn fireknife-btn"
                    aria-label={t('widgetDatePicker.nextMonth')}
                    title={t('widgetDatePicker.nextMonth')}
                >
                    <IconRightArrow className="fireknife-datepicker__header-btn-icon" />
                </button>
            </header>
            <div className="fireknife-datepicker__body">
                <table className="fireknife-datepicker__table">
                    <thead>
                        <tr>
                            {weekdayNames.map((day, index) => (
                                <th key={index} className="fireknife-datepicker__day-name">
                                    {day}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {calendarDays.map((week, i) => (
                            <tr key={i}>{week.map((day) => dayRenderer(day))}</tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};
